import Head from 'next/head'
import { FC } from 'react'

import { serializeJsonLd } from '../jsonLd/graph'
import { buildSeoMeta, SeoMetaInput } from '../seoMeta'
import { medleveIconLinks } from './MedleveIconLinks'

export interface MedleveSeoHeadProps extends SeoMetaInput {
  /** Optional entity graph (`buildPageGraph`) to inline in `<head>`. */
  jsonLd?: unknown
}

/**
 * `<head>` metadata for Medleve ORGANIC pages only: title, description, canonical, robots, Open Graph,
 * Twitter card and (optionally) the JSON-LD graph. Never mount it on paid/campaign routes — they keep
 * `CharlesMeta`. It must be rendered outside any client-only gate to reach the server HTML.
 */
export const MedleveSeoHead: FC<MedleveSeoHeadProps> = ({ jsonLd, ...input }) => {
  const { title, description, canonical, robots, openGraph, twitterCard } = buildSeoMeta(input)
  const { image } = openGraph

  return (
    <Head>
      <title key="title">{title}</title>
      <meta key="description" name="description" content={description} />
      <meta key="robots" name="robots" content={robots} />
      <link key="canonical" rel="canonical" href={canonical} />
      <link key="alternate-pt-BR" rel="alternate" hrefLang="pt-BR" href={canonical} />
      {medleveIconLinks()}

      <meta key="og:type" property="og:type" content={openGraph.type} />
      <meta key="og:site_name" property="og:site_name" content={openGraph.siteName} />
      <meta key="og:locale" property="og:locale" content={openGraph.locale} />
      <meta key="og:url" property="og:url" content={openGraph.url} />
      <meta key="og:title" property="og:title" content={openGraph.title} />
      <meta key="og:description" property="og:description" content={openGraph.description} />
      {image && <meta key="og:image" property="og:image" content={image.url} />}
      {image?.width && <meta key="og:image:width" property="og:image:width" content={String(image.width)} />}
      {image?.height && <meta key="og:image:height" property="og:image:height" content={String(image.height)} />}
      {image?.alt && <meta key="og:image:alt" property="og:image:alt" content={image.alt} />}

      <meta key="twitter:card" name="twitter:card" content={twitterCard} />
      <meta key="twitter:title" name="twitter:title" content={title} />
      <meta key="twitter:description" name="twitter:description" content={description} />
      {image && <meta key="twitter:image" name="twitter:image" content={image.url} />}

      {jsonLd ? (
        <script
          key="medleve-jsonld"
          id="medleve-jsonld"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: serializeJsonLd(jsonLd) }}
        />
      ) : null}
    </Head>
  )
}

export default MedleveSeoHead
